import type { VacancySnapshotForDiff } from './vacancyChangeDiff';

/** Max intake checkpoints stored on vacancy JSON */
export const MAX_INTAKE_VERSION_HISTORY = 30;

export interface VacancyIntakeSnapshot {
  title: string;
  description: string;
  status?: string;
  clientId?: string;
  opdrachtgever_id?: string | null;
  eindklant_id?: string | null;
  shared_with_users?: unknown;
  shared_with_clients?: unknown;
  /** Subset of `vacancy.data` controlled by intake (small JSON) */
  data: Record<string, unknown>;
}

export interface VacancyIntakeVersionEntry {
  id: string;
  savedAt: string;
  changeSummaries: string[];
  userId?: string;
  userName?: string;
  userEmail?: string;
  snapshot: VacancyIntakeSnapshot;
}

/** Keys on `data` that intake save overwrites / owns (overlay on restore). */
export const INTAKE_SNAPSHOT_DATA_KEYS = [
  'jobCode',
  'workplaceType',
  'location',
  'salary',
  'experience',
  'education',
  'benefits',
  'vacancyText',
  'organizationText',
  'category',
  'suggestions',
  'hoursPerWeek',
  'startDate',
  'planningDate',
  'closingDate',
  'contractDuration',
  'contractType',
  'placementTypes',
  'clientName',
  'opdrachtgeverLogoUrl',
  'finalVacancyContent',
  'builtVacancySections',
  /** Vacature Builder (stap Templates) — nodig voor checkpoints bij sync */
  'structuredTemplates',
  'urgencyLevel',
  'campaignChoice',
  'werfplaatsTeamConfig',
  'askWerfplaats',
] as const;

export function pickIntakeSnapshotFromUpdates(updates: {
  title?: string;
  description?: string;
  status?: string;
  clientId?: string;
  opdrachtgever_id?: string | null;
  eindklant_id?: string | null;
  shared_with_users?: unknown;
  shared_with_clients?: unknown;
  data?: Record<string, unknown>;
}): VacancyIntakeSnapshot {
  const d = updates.data || {};
  const picked: Record<string, unknown> = {};
  for (const k of INTAKE_SNAPSHOT_DATA_KEYS) {
    if (Object.prototype.hasOwnProperty.call(d, k)) picked[k] = d[k];
  }
  return {
    title: String(updates.title ?? ''),
    description: String(updates.description ?? ''),
    status: updates.status as string | undefined,
    clientId: updates.clientId,
    opdrachtgever_id: updates.opdrachtgever_id ?? null,
    eindklant_id: updates.eindklant_id ?? null,
    shared_with_users: updates.shared_with_users,
    shared_with_clients: updates.shared_with_clients,
    data: picked,
  };
}

export function snapshotToVacancySnapshotForDiff(s: VacancyIntakeSnapshot): VacancySnapshotForDiff {
  return {
    title: s.title,
    description: s.description,
    data: { ...s.data } as Record<string, unknown>,
    opdrachtgever_id: s.opdrachtgever_id ?? null,
    eindklant_id: s.eindklant_id ?? null,
    shared_with_users: s.shared_with_users,
    shared_with_clients: s.shared_with_clients,
    status: s.status ?? null,
  };
}

/**
 * Intake checkpoints only store a subset of `vacancy.data` keys. Comparing that snapshot to
 * `pendingDiffSnap` (full merged vacancy JSON) falsely marks every marketing field as changed.
 * This normalizes to the same INTAKE_SNAPSHOT_DATA_KEYS set (undefined when absent) for fair diffs.
 */
export function normalizeIntakeSnapshotDataForDiff(data: Record<string, unknown> | undefined): Record<string, unknown> {
  const src = data && typeof data === 'object' ? data : {};
  const o: Record<string, unknown> = {};
  for (const k of INTAKE_SNAPSHOT_DATA_KEYS) {
    o[k] = Object.prototype.hasOwnProperty.call(src, k) ? (src as Record<string, unknown>)[k] : undefined;
  }
  return o;
}

export function intakeSnapshotForHistoryDiff(snap: VacancyIntakeSnapshot): VacancyIntakeSnapshot {
  return {
    ...snap,
    data: normalizeIntakeSnapshotDataForDiff(snap.data),
  };
}

function newVersionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

const snapEq = (a: unknown, b: unknown): boolean => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** Baseline snapshot from loaded vacancy (for first diff after open / refresh). */
export function intakeBaselineSnapshotFromVacancy(vacancy: {
  title?: string;
  description?: string;
  status?: string;
  clientId?: string;
  client_id?: string;
  opdrachtgever_id?: string | null;
  eindklant_id?: string | null;
  shared_with_users?: unknown;
  shared_with_clients?: unknown;
  data?: Record<string, unknown> | null;
}): VacancyIntakeSnapshot {
  return pickIntakeSnapshotFromUpdates({
    title: vacancy.title,
    description: vacancy.description,
    status: vacancy.status as string | undefined,
    clientId: vacancy.clientId ?? vacancy.client_id,
    opdrachtgever_id: vacancy.opdrachtgever_id ?? null,
    eindklant_id: vacancy.eindklant_id ?? null,
    shared_with_users: vacancy.shared_with_users,
    shared_with_clients: vacancy.shared_with_clients,
    data: { ...(vacancy.data || {}) },
  });
}

export function intakeSnapshotsEqual(a: VacancyIntakeSnapshot, b: VacancyIntakeSnapshot): boolean {
  if (!snapEq(a.title, b.title)) return false;
  if (!snapEq(a.description, b.description)) return false;
  if (!snapEq(a.status, b.status)) return false;
  if (!snapEq(a.clientId, b.clientId)) return false;
  if (!snapEq(a.opdrachtgever_id, b.opdrachtgever_id)) return false;
  if (!snapEq(a.eindklant_id, b.eindklant_id)) return false;
  if (!snapEq(a.shared_with_users, b.shared_with_users)) return false;
  if (!snapEq(a.shared_with_clients, b.shared_with_clients)) return false;
  return snapEq(pickIntakeKeysFromData(a.data || {}), pickIntakeKeysFromData(b.data || {}));
}

export function lastIntakeHistorySnapshot(
  data: Record<string, unknown> | undefined | null
): VacancyIntakeSnapshot | null {
  const h = data?.intakeVersionHistory;
  if (!Array.isArray(h) || h.length === 0) return null;
  const last = h[h.length - 1] as VacancyIntakeVersionEntry | undefined;
  return last?.snapshot ?? null;
}

/** Pick intake-owned keys from a merged `vacancy.data` object (for Flow state patches). */
export function pickIntakeKeysFromData(data: Record<string, unknown>): Record<string, unknown> {
  const o: Record<string, unknown> = {};
  for (const k of INTAKE_SNAPSHOT_DATA_KEYS) {
    if (Object.prototype.hasOwnProperty.call(data, k)) o[k] = data[k];
  }
  return o;
}

/**
 * Appends one intake checkpoint to `data.intakeVersionHistory` (immutable).
 * Called after each successful intake save (uses fallback line if diff is empty).
 */
export function appendIntakeVersionHistory(
  data: Record<string, unknown> | undefined,
  snapshot: VacancyIntakeSnapshot,
  changeSummaries: string[],
  actor?: { id?: string; name?: string; email?: string }
): Record<string, unknown> {
  const lines =
    changeSummaries.filter(Boolean).length > 0
      ? changeSummaries.filter(Boolean)
      : ['Opgeslagen — geen automatische diff t.o.v. databaseversie.'];

  const base: Record<string, unknown> = { ...(data || {}) };
  const prev = Array.isArray(base.intakeVersionHistory)
    ? ([...base.intakeVersionHistory] as VacancyIntakeVersionEntry[])
    : [];

  const row: VacancyIntakeVersionEntry = {
    id: newVersionId(),
    savedAt: new Date().toISOString(),
    changeSummaries: lines,
    userId: actor?.id,
    userName: actor?.name,
    userEmail: actor?.email,
    snapshot,
  };
  prev.push(row);
  base.intakeVersionHistory = prev.slice(-MAX_INTAKE_VERSION_HISTORY);
  return base;
}

/** Merge snapshot onto current vacancy data without dropping AI / flow history. */
export function mergeIntakeSnapshotIntoVacancy(
  vacancy: { data?: Record<string, unknown> | null },
  snap: VacancyIntakeSnapshot
): Record<string, unknown> {
  const cur = { ...(vacancy.data || {}) };
  for (const k of INTAKE_SNAPSHOT_DATA_KEYS) {
    if (Object.prototype.hasOwnProperty.call(snap.data, k)) (cur as any)[k] = snap.data[k];
  }
  return cur;
}

export type IntakeFormShape = {
  title: string;
  jobCode: string;
  workplaceType: string;
  location: string;
  description: string;
  requirements: string;
  benefits: string;
  salary: { min: number; max: number; currency: string; period: string };
  experience: string;
  education: string;
  employmentType: string;
  hoursPerWeek: string;
  clientId: string;
  opdrachtgever_id: string;
  eindklant_id: string;
  organizationText: string;
  startDate: string;
  planningDate: string;
  closingDate: string;
  contractDuration: string;
  contractType: string;
  placementTypes: string[];
  sharedWithClients: string[];
  sharedWithUsers: string[];
  askWerfplaats: boolean;
  urgencyLevel: string;
  campaignChoice: string;
  werfplaatsTeamConfig: { leads: number; selection: string; interview: string; support: string };
};

/** Intake-editor bron: altijd `vacancyText`, niet de gebouwde vacature uit de Builder. */
export function intakeBriefingTextFromVacancy(vacancy: {
  description?: string;
  data?: Record<string, unknown> | null;
}): string {
  const d = vacancy.data || {};
  const vacancyText = d.vacancyText;
  if (typeof vacancyText === 'string' && vacancyText.trim()) return vacancyText;
  return vacancy.description || '';
}

/** Of de vacature nog een volledige fetch nodig heeft (lijstpayload mist description/vacancyText). */
export function vacancyNeedsFullContentFetch(vacancy: {
  id?: string;
  description?: string;
  data?: Record<string, unknown> | null;
  _isLight?: boolean;
} | null | undefined): boolean {
  if (!vacancy?.id) return false;
  if (vacancy._isLight) return true;
  return !String(intakeBriefingTextFromVacancy(vacancy)).trim();
}

/**
 * Lijst-/realtime-update mag geen description of intake-tekst van een open vacature wissen.
 */
export function mergeVacancyPreserveHeavyContent<T extends Record<string, unknown>>(
  prev: T,
  incoming: T
): T {
  const prevData = (prev.data || {}) as Record<string, unknown>;
  const inData = (incoming.data || {}) as Record<string, unknown>;
  const description =
    String(incoming.description || '').trim() ||
    String(prev.description || '').trim() ||
    '';
  const vacancyText =
    typeof inData.vacancyText === 'string' && inData.vacancyText.trim()
      ? inData.vacancyText
      : prevData.vacancyText;
  const finalVacancyContent =
    typeof inData.finalVacancyContent === 'string' && inData.finalVacancyContent.trim()
      ? inData.finalVacancyContent
      : prevData.finalVacancyContent;
  const builtVacancySections =
    Array.isArray(inData.builtVacancySections) && inData.builtVacancySections.length
      ? inData.builtVacancySections
      : prevData.builtVacancySections;

  return {
    ...incoming,
    description: description || prev.description,
    data: {
      ...prevData,
      ...inData,
      ...(vacancyText !== undefined ? { vacancyText } : {}),
      ...(finalVacancyContent !== undefined ? { finalVacancyContent } : {}),
      ...(builtVacancySections !== undefined ? { builtVacancySections } : {}),
    },
    _isLight: Boolean((incoming as { _isLight?: boolean })._isLight) && !description,
  } as T;
}

export function formatVacancyRowFromSupabase(fullVac: Record<string, unknown>): Record<string, unknown> {
  const data = (fullVac.data || {}) as Record<string, unknown>;
  return {
    ...fullVac,
    clientId: fullVac.client_id,
    createdAt: fullVac.created_at
      ? new Date(String(fullVac.created_at)).toLocaleDateString('nl-NL')
      : undefined,
    client: fullVac.clients || fullVac.client,
    eindklant: fullVac.eindklant,
    opdrachtgever: fullVac.opdrachtgever,
    data: { ...data, tags: data.tags || [], category: data.category || '' },
    _isLight: false,
  };
}

export function formDataFromVacancy(vacancy: {
  title?: string;
  description?: string;
  status?: string;
  clientId?: string;
  client_id?: string;
  opdrachtgever_id?: string | null;
  eindklant_id?: string | null;
  shared_with_users?: unknown;
  shared_with_clients?: unknown;
  data?: Record<string, unknown> | null;
}): IntakeFormShape {
  const d = vacancy.data || {};
  const salary = (d.salary as IntakeFormShape['salary']) || {
    min: 0,
    max: 0,
    currency: 'EUR',
    period: 'Yearly',
  };
  const wtc = (d.werfplaatsTeamConfig as IntakeFormShape['werfplaatsTeamConfig']) || {
    leads: 50,
    selection: 'Alle profielen',
    interview: 'Zelf Doen',
    support: 'Geen Hulp',
  };
  return {
    title: vacancy.title || '',
    jobCode: String(d.jobCode ?? ''),
    workplaceType: String(d.workplaceType ?? 'On-site'),
    location: String(d.location ?? ''),
    description: intakeBriefingTextFromVacancy(vacancy),
    requirements: String(d.suggestions ?? ''),
    benefits: String(d.benefits ?? ''),
    salary,
    experience: String(d.experience ?? 'Medior'),
    education: String(d.education ?? 'HBO'),
    employmentType: String(d.category ?? 'Fulltime'),
    hoursPerWeek: String(d.hoursPerWeek ?? 'In overleg'),
    clientId: vacancy.clientId || (vacancy as { client_id?: string }).client_id || '',
    opdrachtgever_id: vacancy.opdrachtgever_id || String(d.opdrachtgever_id ?? ''),
    eindklant_id: vacancy.eindklant_id || '',
    organizationText: String(d.organizationText ?? ''),
    startDate: String(d.startDate ?? ''),
    planningDate: String(d.planningDate ?? ''),
    closingDate: String(d.closingDate ?? ''),
    contractDuration: String(d.contractDuration ?? ''),
    contractType: String(d.contractType ?? ''),
    placementTypes: Array.isArray(d.placementTypes) ? [...(d.placementTypes as string[])] : [],
    sharedWithClients: Array.isArray(vacancy.shared_with_clients)
      ? [...(vacancy.shared_with_clients as string[])]
      : [],
    sharedWithUsers: Array.isArray(vacancy.shared_with_users)
      ? [...(vacancy.shared_with_users as string[])]
      : [],
    askWerfplaats: Boolean((d as { askWerfplaats?: boolean }).askWerfplaats),
    urgencyLevel: String(d.urgencyLevel ?? 'Medium Priority'),
    campaignChoice: String(d.campaignChoice ?? 'werfplaats'),
    werfplaatsTeamConfig: wtc,
  };
}

export function buildPendingIntakeSnapshot(
  form: IntakeFormShape,
  vacancy: { status?: string; clientId?: string; data?: Record<string, unknown> | null },
  clients: { id: string; name?: string; logo_url?: string }[]
): VacancyIntakeSnapshot {
  const combinedDescription = `
${form.organizationText ? `<h2>Over de organisatie</h2><p>${form.organizationText}</p>` : ''}
${form.description}
  `.trim();
  const og = clients.find((c) => c.id === form.opdrachtgever_id);
  const existingBuilt = Array.isArray(vacancy.data?.builtVacancySections)
    ? (vacancy.data!.builtVacancySections as { title: string; content: string }[])
    : [];
  const data: Record<string, unknown> = {
    jobCode: form.jobCode,
    workplaceType: form.workplaceType,
    location: form.location,
    salary: form.salary,
    experience: form.experience,
    education: form.education,
    benefits: form.benefits,
    vacancyText: form.description,
    organizationText: form.organizationText,
    category: form.employmentType,
    suggestions: form.requirements,
    hoursPerWeek: form.hoursPerWeek,
    startDate: form.startDate,
    planningDate: form.planningDate,
    closingDate: form.closingDate,
    contractDuration: form.contractDuration,
    contractType: form.contractType,
    placementTypes: form.placementTypes,
    clientName: og?.name || (vacancy.data as any)?.clientName,
    opdrachtgeverLogoUrl: og?.logo_url || (vacancy.data as any)?.opdrachtgeverLogoUrl,
    finalVacancyContent:
      existingBuilt.length > 0
        ? existingBuilt.map((s) => `<h3>${s.title}</h3>${s.content}`).join('\n\n')
        : combinedDescription,
    builtVacancySections: existingBuilt.length > 0 ? existingBuilt.map((s) => ({ ...s })) : [],
    urgencyLevel: form.urgencyLevel as any,
    campaignChoice: form.campaignChoice as any,
    werfplaatsTeamConfig: form.werfplaatsTeamConfig,
    askWerfplaats: form.askWerfplaats,
  };
  const publishedDescription =
    existingBuilt.length > 0
      ? existingBuilt.map((s) => `<h3>${s.title}</h3>${s.content}`).join('\n\n')
      : combinedDescription;

  return {
    title: form.title,
    description: publishedDescription,
    status: (vacancy as any).status,
    clientId: form.clientId || (vacancy as any).client_id,
    opdrachtgever_id: form.opdrachtgever_id || null,
    eindklant_id: form.eindklant_id || null,
    shared_with_users: form.sharedWithUsers,
    shared_with_clients: form.sharedWithClients,
    data,
  };
}

export function intakeSnapshotToFormData(snap: VacancyIntakeSnapshot): IntakeFormShape {
  const d = snap.data || {};
  const salary = (d.salary as IntakeFormShape['salary']) || {
    min: 0,
    max: 0,
    currency: 'EUR',
    period: 'Yearly',
  };
  const wtc = (d.werfplaatsTeamConfig as IntakeFormShape['werfplaatsTeamConfig']) || {
    leads: 50,
    selection: 'Alle profielen',
    interview: 'Zelf Doen',
    support: 'Geen Hulp',
  };
  return {
    title: snap.title || '',
    jobCode: String(d.jobCode ?? ''),
    workplaceType: String(d.workplaceType ?? 'On-site'),
    location: String(d.location ?? ''),
    description: String(d.vacancyText ?? ''),
    requirements: String(d.suggestions ?? ''),
    benefits: String(d.benefits ?? ''),
    salary,
    experience: String(d.experience ?? 'Medior'),
    education: String(d.education ?? 'HBO'),
    employmentType: String(d.category ?? 'Fulltime'),
    hoursPerWeek: String(d.hoursPerWeek ?? 'In overleg'),
    clientId: String(snap.clientId ?? ''),
    opdrachtgever_id: String(snap.opdrachtgever_id ?? ''),
    eindklant_id: String(snap.eindklant_id ?? ''),
    organizationText: String(d.organizationText ?? ''),
    startDate: String(d.startDate ?? ''),
    planningDate: String(d.planningDate ?? ''),
    closingDate: String(d.closingDate ?? ''),
    contractDuration: String(d.contractDuration ?? ''),
    contractType: String(d.contractType ?? ''),
    placementTypes: Array.isArray(d.placementTypes) ? [...(d.placementTypes as string[])] : [],
    sharedWithClients: Array.isArray(snap.shared_with_clients)
      ? [...(snap.shared_with_clients as string[])]
      : [],
    sharedWithUsers: Array.isArray(snap.shared_with_users) ? [...(snap.shared_with_users as string[])] : [],
    askWerfplaats: Boolean((d as any).askWerfplaats),
    urgencyLevel: String(d.urgencyLevel ?? 'Medium Priority'),
    campaignChoice: String(d.campaignChoice ?? 'werfplaats'),
    werfplaatsTeamConfig: wtc,
  };
}
